import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Pencil } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { extractApiErrorMessage } from "@/lib/api-error";
import {
  INGREDIENTS_QUERY_KEY,
  useIngredients,
  type Ingredient,
} from "./IngredientsManagerDialog";

const CREATE_NEW_VALUE = "__create_new__";

interface Props {
  // Snapshot do nome selecionado (vem do option.name).
  value: string;
  ingredientId?: string;
  onChange: (next: { name: string; ingredientId: string }) => void;
  // Disparado ao clicar no lápis. Caller deve salvar o produto e abrir o
  // popup de gestão de ingredientes.
  onEditClick: () => void;
  className?: string;
}

export function IngredientSelectField({
  value,
  ingredientId,
  onChange,
  onEditClick,
  className,
}: Props): JSX.Element {
  const { data: ingredients = [] } = useIngredients();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [creating, setCreating] = useState(false);

  const createMutation = useMutation({
    mutationFn: (name: string) =>
      customFetch<Ingredient>("/api/ingredients", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, isActive: true, stockQuantity: null }),
      }),
    onSuccess: (ing) => {
      queryClient.invalidateQueries({ queryKey: INGREDIENTS_QUERY_KEY });
      onChange({ name: ing.name, ingredientId: ing.id });
      setCreating(false);
    },
    onError: (err: any) => {
      const msg = extractApiErrorMessage(err, "Não foi possível criar o ingrediente.");
      toast({ title: "Erro", description: msg, variant: "destructive" });
      setCreating(false);
    },
  });

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    if (v === CREATE_NEW_VALUE) {
      if (creating) return;
      const name = prompt("Nome do novo ingrediente:");
      if (!name || !name.trim()) return;
      setCreating(true);
      createMutation.mutate(name.trim());
      return;
    }
    const ing = ingredients.find((i) => i.id === v);
    if (ing) {
      onChange({ name: ing.name, ingredientId: ing.id });
    } else if (v === "") {
      onChange({ name: "", ingredientId: "" });
    }
  };

  const selectValue = ingredientId
    ? ingredients.some((i) => i.id === ingredientId) ? ingredientId : ""
    : "";

  return (
    <div className={`flex items-center gap-1 min-w-0 ${className ?? ""}`}>
      <select
        value={selectValue}
        onChange={handleSelectChange}
        disabled={createMutation.isPending}
        className="flex-1 min-w-0 h-8 rounded-md border border-input bg-card px-2 text-sm"
      >
        <option value="">selecione um ingrediente</option>
        {ingredients.map((ing) => (
          <option key={ing.id} value={ing.id}>
            {ing.name}
            {!ing.isActive ? " (inativo)" : ""}
            {ing.stockQuantity === 0 ? " (esgotado)" : ""}
          </option>
        ))}
        <option value={CREATE_NEW_VALUE}>+ Novo Ingrediente</option>
      </select>
      {ingredientId && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onEditClick}
          title="Editar / gerenciar ingredientes"
        >
          <Pencil className="w-3.5 h-3.5" />
        </Button>
      )}
      {/* Mantém o nome snapshot pra acessibilidade — se ingredientId existe
          mas o ingrediente sumiu da lista (deletado), o texto continua visível
          como fallback no select via `value` vazio. */}
      {value && !selectValue && (
        <span className="text-xs text-destructive ml-1" title="Ingrediente não está mais disponível">
          {value} (removido)
        </span>
      )}
    </div>
  );
}
